import { useNavigate, useParams } from 'react-router-dom'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import {
  Card,
  Descriptions,
  Button,
  Space,
  Tag,
  Alert,
  Popconfirm,
  message,
  Breadcrumb,
} from 'antd'
import { EditOutlined, DeleteOutlined, TeamOutlined, KeyOutlined } from '@ant-design/icons'
import { adminUsersApi } from '@/api/admin-users'
import { useAuth } from '@/hooks/useAuth'
import { PageLoading } from '@/components/PageLoading'
import { getApiErrorMessage } from '@/utils/error'

export const AdminUserDetail = () => {
  const navigate = useNavigate()
  const { email } = useParams<{ email: string }>()
  const queryClient = useQueryClient()
  const { user: currentUser } = useAuth()

  // Fetch user data (enabled only when email exists)
  const { data, isLoading, error } = useQuery({
    queryKey: ['admin-user', email],
    queryFn: () => adminUsersApi.getAdminUser(email!),
    enabled: !!email,
  })

  // Delete mutation
  const deleteMutation = useMutation({
    mutationFn: adminUsersApi.deleteAdminUser,
    onSuccess: () => {
      message.success('Admin user deleted successfully')
      queryClient.invalidateQueries({ queryKey: ['admin-users'] })
      navigate('/admin/users')
    },
    onError: (error: unknown) => {
      message.error(getApiErrorMessage(error, 'Failed to delete admin user'))
    },
  })

  // Reset password mutation
  const resetPasswordMutation = useMutation({
    mutationFn: adminUsersApi.resetPassword,
    onSuccess: () => {
      message.success('Password reset email sent successfully')
    },
    onError: (error: unknown) => {
      message.error(getApiErrorMessage(error, 'Failed to reset password'))
    },
  })

  // Handle missing email after all hooks
  if (!email) {
    navigate('/admin/users')
    return null
  }

  const handleDelete = () => {
    deleteMutation.mutate(email)
  }

  const handleResetPassword = () => {
    resetPasswordMutation.mutate(email)
  }

  if (isLoading) {
    return <PageLoading />
  }

  if (error || !data?.data.user) {
    return (
      <Card>
        <Alert
          message="Error"
          description="Failed to load admin user details"
          type="error"
          showIcon
        />
      </Card>
    )
  }

  const user = data.data.user
  const isCurrentUser = currentUser?.email === user.email

  const roleColors: Record<string, string> = {
    ADMIN: 'red',
    SUPPORT: 'blue',
    VIEWER: 'green',
  }

  return (
    <div>
      <Breadcrumb
        style={{ marginBottom: 16 }}
        items={[
          {
            title: (
              <a onClick={() => navigate('/admin/users')}>
                <TeamOutlined /> Admin Users
              </a>
            ),
          },
          {
            title: user.email,
          },
        ]}
      />
      <Card
        title="Admin User Details"
        extra={
          <Space>
            <Button
              type="primary"
              icon={<EditOutlined />}
              onClick={() => navigate(`/admin/users/${email}/edit`)}
            >
              Edit
            </Button>
            <Popconfirm
              title="Reset password"
              description="Send password reset email to this user?"
              onConfirm={handleResetPassword}
              okText="Yes"
              cancelText="No"
            >
              <Button icon={<KeyOutlined />} loading={resetPasswordMutation.isPending}>
                Reset Password
              </Button>
            </Popconfirm>
            {!isCurrentUser && (
              <Popconfirm
                title="Delete admin user"
                description="Are you sure you want to delete this admin user? This action cannot be undone."
                onConfirm={handleDelete}
                okText="Yes"
                cancelText="No"
              >
                <Button danger icon={<DeleteOutlined />} loading={deleteMutation.isPending}>
                  Delete
                </Button>
              </Popconfirm>
            )}
          </Space>
        }
      >
        <Descriptions bordered column={1}>
          <Descriptions.Item label="Email">{user.email}</Descriptions.Item>
          <Descriptions.Item label="Name">
            {user.given_name} {user.family_name}
          </Descriptions.Item>
          <Descriptions.Item label="Role">
            <Tag color={roleColors[user.role]}>{user.role}</Tag>
          </Descriptions.Item>
          <Descriptions.Item label="Status">
            <Tag color={user.status === 'CONFIRMED' ? 'green' : 'orange'}>
              {user.status || 'N/A'}
            </Tag>
          </Descriptions.Item>
          <Descriptions.Item label="Email Verified">
            <Tag color={user.email_verified ? 'green' : 'red'}>
              {user.email_verified ? 'Yes' : 'No'}
            </Tag>
          </Descriptions.Item>
          <Descriptions.Item label="User ID">{user.sub}</Descriptions.Item>
          {user.enabled !== undefined && (
            <Descriptions.Item label="Enabled">
              <Tag color={user.enabled ? 'green' : 'red'}>{user.enabled ? 'Yes' : 'No'}</Tag>
            </Descriptions.Item>
          )}
          {user.created && (
            <Descriptions.Item label="Created">
              {new Date(user.created).toLocaleString()}
            </Descriptions.Item>
          )}
          {user.modified && (
            <Descriptions.Item label="Modified">
              {new Date(user.modified).toLocaleString()}
            </Descriptions.Item>
          )}
        </Descriptions>
      </Card>
    </div>
  )
}
